import React, { FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { getReferenceProgress } from './utils';
import { IAppState } from '../../../../common/models/store/IAppState';
import { IRefReqCounters } from '../../../../common/models/referenceRequest/IRefReqCounters';
import ReferenceNameForm from '../../components/ReferenceNameForm';
import ReferenceContactInfoForm from '../../components/ReferenceContactInfoForm';
import ReferenceCompanyForm from '../../components/ReferenceCompanyForm';
import { ReferenceType } from '../../../../common/enums/ReferenceType';
import { IBindingCallback1, IBindingAction } from '../../../../common/models/callback';
import { IReferenceCandidateForm } from '../../../../common/models/reference/IReferenceCandidateForm';
import { checkPhoneNumberRoutine, setCurrentReferenceRoutine, setReferencesRoutine } from '../../routines';
import Overview from '../../components/Overview';
import ReferenceRoleForm from '../../../../components/ReferenceRoleForm';
import Final from '../../components/Final';
import ProgressBar from '../../components/ProgressBar';
import { RefFormPages } from '../CandidateForm/utils';
import styles from './styles.module.scss';

interface IProps {
  references: IReferenceCandidateForm[];
  currentReference: IReferenceCandidateForm;
  counters: IRefReqCounters;
  disabledTypes: ReferenceType[];
  currentPage: RefFormPages;
  logo: string;
  onGoBack: IBindingAction;
  onContinue: IBindingAction;
  setCurrentReference: IBindingCallback1<IReferenceCandidateForm>;
  setReferences: IBindingCallback1<IReferenceCandidateForm[]>;
  setCurrentPage: (page: RefFormPages, goBack?: boolean) => void;
  checkPhoneNumber: IBindingCallback1<{ onSuccess: IBindingAction; phoneNumber: string }>
}

export const ReferenceForm: FunctionComponent<IProps> = ({
  logo,
  counters,
  references,
  currentPage,
  disabledTypes,
  currentReference,
  onGoBack,
  onContinue,
  setReferences,
  setCurrentPage,
  setCurrentReference,
  checkPhoneNumber
}) => {
  const isFullReference = Boolean(
    currentReference.id
    && currentReference.firstName
    && currentReference.type
    && (currentReference.email || currentReference.phoneNumber)
    && currentReference.workInCompany
  );

  const addReference = (callback: IBindingAction) => {
    if (isFullReference) {
      setReferences([...references, currentReference]);
    }
    callback();
  };

  const setRefContactInfo = (email: string | null, phoneNumber: string | null) => {
    setCurrentReference({ ...currentReference, email, phoneNumber });
    setCurrentPage(RefFormPages.ReferenceCommonCompany);
  };

  const submitContactInfo = (email: string | null, phoneNumber: string | null) => {
    if (phoneNumber && currentReference.phoneNumber !== phoneNumber) {
      checkPhoneNumber({ onSuccess: () => setRefContactInfo(email, phoneNumber), phoneNumber });
    } else {
      setRefContactInfo(email, phoneNumber);
    }
  };

  const getCurrentPage = () => {
    switch (currentPage) {
      case RefFormPages.Overview:
        return (
          <Overview
            onGoBack={onGoBack}
            refCounters={counters}
            onContinue={() => setCurrentPage(RefFormPages.ReferenceName)}
          />
        );
      case RefFormPages.ReferenceName:
        return (
          <ReferenceNameForm
            value={{
              id: currentReference?.id,
              firstName: currentReference?.firstName,
              lastName: currentReference?.lastName
            }}
            references={references}
            onGoBack={onGoBack}
            onSubmit={({ id, firstName, lastName }) => {
              setCurrentReference({ ...currentReference, id, firstName, lastName });
              setCurrentPage(RefFormPages.ReferenceRole);
            }}
          />
        );
      case RefFormPages.ReferenceRole:
        return (
          <ReferenceRoleForm
            disabledTypes={disabledTypes}
            references={references}
            counters={counters}
            currentReference={currentReference}
            onGoBack={onGoBack}
            onContinue={({ type, otherType }) => {
              setCurrentReference({ ...currentReference, type, otherType });
              setCurrentPage(RefFormPages.ReferenceContactInfo);
            }}
          />
        );
      case RefFormPages.ReferenceContactInfo:
        return (
          <ReferenceContactInfoForm
            value={{
              id: currentReference.id,
              email: currentReference.email,
              phoneNumber: currentReference.phoneNumber
            }}
            references={references}
            currentReference={currentReference}
            onGoBack={onGoBack}
            onSubmit={({ email, phoneNumber }) => submitContactInfo(email, phoneNumber)}
          />
        );
      case RefFormPages.ReferenceCommonCompany:
        return (
          <ReferenceCompanyForm
            value={{
              id: currentReference.id,
              workInCompany: currentReference.workInCompany,
              workStartDate: currentReference.workStartDate,
              workEndDate: currentReference.workEndDate
            }}
            firstName={currentReference.firstName}
            onGoBack={onGoBack}
            onSubmit={({ workInCompany, workStartDate, workEndDate }) => {
              setCurrentReference({ ...currentReference, workInCompany, workStartDate, workEndDate });
              setCurrentPage(RefFormPages.Final);
            }}
          />
        );
      case RefFormPages.Final:
        return (
          <Final
            references={references}
            currentReference={currentReference}
            counters={counters}
            onGoBack={onGoBack}
            onFinish={() => addReference(onContinue)}
            onAddMore={() => addReference(() => setCurrentPage(RefFormPages.ReferenceName))}
          />
        );
      default:
        return null;
    }
  };

  const Header = () => (
    <div className={styles.header}>
      {logo && (
        <img className={styles.logo} src={logo} alt="" />
      )}
      <div className={styles.progress}>
        <FormattedMessage id="reference" defaultMessage="Reference" />
        &nbsp;
        {references.length + 1}
        &nbsp;
        <FormattedMessage id="of" />
        &nbsp;
        {counters.totalCount}
        &nbsp;
        <ProgressBar value={getReferenceProgress(currentReference)} max={1} className="cf-progress" />
      </div>
    </div>
  );

  return (
    <div className={styles.referenceForm}>
      {![RefFormPages.Overview, RefFormPages.Final].includes(currentPage) && <Header />}
      {getCurrentPage()}
    </div>
  );
};

const mapStateToProps = ({
  referenceRequest: {
    disabledTypes,
    refCounters,
    references,
    currentReference
  }
}: IAppState) => ({ counters: refCounters, disabledTypes, references, currentReference });

const mapDispachToProps = {
  setReferences: setReferencesRoutine,
  setCurrentReference: setCurrentReferenceRoutine,
  checkPhoneNumber: checkPhoneNumberRoutine
};

export default connect(mapStateToProps, mapDispachToProps)(ReferenceForm);
